import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring, useReducedMotion } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { AMAZON_URL, BOOK2_COVER, BOOK2_URL, BOOK2_ASIN, AURORA_BG } from "@/constants/site";
import { MaskedLine, EASE } from "./Reveal";

export const Hero = () => {
  const heroRef = useRef(null);
  const coverRef = useRef(null);
  const reduced = useReducedMotion();

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", reduced ? "0%" : "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, reduced ? 1.05 : 1.2]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", reduced ? "0%" : "-14%"]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 110, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 110, damping: 18 });

  const onMove = (e) => {
    if (reduced || !coverRef.current) return;
    const r = coverRef.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  const goBookOne = (e) => {
    e.preventDefault();
    const el = document.querySelector("#book");
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section ref={heroRef} id="top" className="relative min-h-screen overflow-hidden flex flex-col justify-center">
      {/* Parallax aurora backdrop */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{ y: bgY, scale: bgScale }}
        aria-hidden="true"
      >
        <img
          src={AURORA_BG}
          alt=""
          className="w-full h-full object-cover opacity-60"
          loading="eager"
        />
        <div className="absolute inset-0 bg-black/45" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/80 via-transparent to-[#050505]" />
        <div
          className="aurora-ribbon left-[-10%] top-[18%] w-[70%] h-[26%]"
          style={{ background: "radial-gradient(ellipse at center, rgba(56,189,248,0.35), transparent 65%)" }}
        />
        <div
          className="aurora-ribbon right-[-15%] top-[40%] w-[65%] h-[24%]"
          style={{ background: "radial-gradient(ellipse at center, rgba(16,185,129,0.28), transparent 65%)", animationDuration: "19s", animationDelay: "-6s" }}
        />
      </motion.div>

      <motion.div
        style={{ y: textY }}
        className="relative max-w-[1400px] w-full mx-auto px-6 lg:px-12 pt-32 pb-28 grid lg:grid-cols-12 gap-16 items-center"
      >
        {/* Left — kinetic type */}
        <div className="lg:col-span-7">
          <MaskedLine delay={0.1}>
            <span className="font-tech text-[11px] uppercase tracking-[0.3em] text-sky-400 flex items-center gap-3">
              <span className="w-2 h-2 bg-sky-400 animate-pulse" />
              The Aurora Chronicles — Book Two · Out Now
            </span>
          </MaskedLine>

          <h1 className="font-display font-light leading-[0.95] tracking-tighter text-[clamp(2.9rem,7.2vw,7rem)] mt-8">
            <MaskedLine delay={0.25}>The storm has</MaskedLine>
            <MaskedLine delay={0.4}>
              <span className="italic bg-gradient-to-r from-sky-400 via-teal-300 to-emerald-400 bg-clip-text text-transparent">
                broken.
              </span>
            </MaskedLine>
            <MaskedLine delay={0.55}>
              <span className="text-zinc-300">Now the whole country</span>
            </MaskedLine>
            <MaskedLine delay={0.7}>
              <span className="text-zinc-300">will feel it.</span>
            </MaskedLine>
          </h1>

          <MaskedLine delay={0.9}>
            <span className="block mt-10 max-w-xl text-zinc-300 font-light leading-relaxed text-base md:text-lg">
              Aurora Unbound is here. Across Canada, others like Alex awaken — friends or
              rivals in a fight against control. Order Book Two today, or catch up on where
              it all began.
            </span>
          </MaskedLine>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
            className="mt-12 flex flex-wrap items-center gap-6"
          >
            <a
              href={BOOK2_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-buy-button"
              className="group inline-flex items-center gap-3 bg-sky-500 text-black font-tech text-xs uppercase tracking-[0.25em] px-8 py-4 hover:bg-sky-400 hover:-translate-y-0.5 transition-[background-color,transform] duration-300"
            >
              Order now — $29.99
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href="#book"
              onClick={goBookOne}
              data-testid="hero-start-book-one"
              className="inline-flex items-center gap-3 font-tech text-xs uppercase tracking-[0.25em] text-zinc-300 border border-white/15 px-8 py-4 hover:border-emerald-400/60 hover:text-emerald-300 transition-[border-color,color] duration-300"
            >
              Start with Book One
              <ArrowDown size={16} />
            </a>
          </motion.div>

          <MaskedLine delay={1.2}>
            <span className="block mt-12 font-tech text-[10px] uppercase tracking-[0.3em] text-zinc-500" data-testid="hero-prices">
              Book Two available now &nbsp;·&nbsp; Book One from $24.99 &nbsp;·&nbsp; On Amazon
            </span>
          </MaskedLine>
        </div>

        {/* Right — Book Two cover with 3D tilt + spotlight */}
        <motion.div
          className="lg:col-span-5 flex justify-center lg:justify-end"
          initial={{ opacity: 0, y: 48 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.8, ease: EASE }}
        >
          <div
            ref={coverRef}
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            className="relative"
            data-testid="hero-book-cover"
          >
            <div
              className="absolute -inset-16 pointer-events-none"
              style={{ background: "radial-gradient(ellipse at center, rgba(56,189,248,0.25), rgba(16,185,129,0.10) 45%, transparent 70%)", filter: "blur(30px)" }}
              aria-hidden="true"
            />
            <motion.div
              style={{ rotateX: reduced ? 0 : rx, rotateY: reduced ? 0 : ry, transformPerspective: 1000 }}
              className="relative clip-frame border border-white/10 bg-[#0a0a0a] shadow-[0_40px_120px_-20px_rgba(56,189,248,0.40)]"
            >
              <img
                src={BOOK2_COVER}
                alt="Aurora Unbound — The Aurora Chronicles, Book Two, by Michael B. Ford, front cover"
                className="w-56 sm:w-72 lg:w-[380px] block"
                loading="eager"
              />
              <div className="absolute inset-0 pointer-events-none bg-gradient-to-tr from-transparent via-white/5 to-white/10" />
            </motion.div>
            <div className="absolute -bottom-10 left-0 font-tech text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              ASIN {BOOK2_ASIN} &nbsp;·&nbsp; Available now
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-6 lg:left-12 flex items-center gap-4"
        data-testid="hero-scroll-cue"
      >
        <span className="font-tech text-[10px] uppercase tracking-[0.35em] text-zinc-500">Scroll</span>
        <span className="block w-px h-10 bg-gradient-to-b from-sky-400 to-transparent scroll-pulse" />
      </motion.div>
    </section>
  );
};

export default Hero;
